import TypeDefaultResponse from "../types/TypeDefaultResponse";

export type UploadUrlRequest = {
  originalName: string;
  mimeType: string;
  size: number;
};

export type UploadUrlResponse = TypeDefaultResponse & {
  url?: string;
  filepath?: string;
};

export type SignedReadUrlResponse = TypeDefaultResponse & {
  url?: string;
};

export type StoredFileView = {
  _id?: string;
  filepath?: string;
  filename?: string;
  type?: string;
  size?: number;
  link?: string;
};

export type BadgeFileView = {
  file?: StoredFilePreviewSource;
  fileS3?: string;
  link?: string;
};

export type StoredFilePreviewSource =
  | File
  | StoredFileView
  | string
  | null
  | undefined;

export const getFileMimeType = (file: File) =>
  file.type || "application/octet-stream";

export const getS3FirstValue = <TStored, TLegacy>({
  storedFileReference,
  legacyReference,
}: {
  storedFileReference?: TStored | null;
  legacyReference?: TLegacy | null;
}) => storedFileReference ?? legacyReference ?? undefined;

export const uploadStoredFileWithPresignedUrl = async ({
  file,
  getUploadUrl,
}: {
  file: File;
  getUploadUrl: (body: UploadUrlRequest) => Promise<UploadUrlResponse>;
}) => {
  const upload = await getUploadUrl({
    originalName: file.name,
    mimeType: getFileMimeType(file),
    size: file.size,
  });

  if (!upload.status || !upload.url || !upload.filepath) {
    throw new Error(upload.message ?? "Failed to prepare upload");
  }

  const response = await fetch(upload.url, {
    method: "PUT",
    headers: { "Content-Type": getFileMimeType(file) },
    body: file,
  });

  if (!response.ok) {
    throw new Error("Failed to upload file");
  }

  return upload.filepath;
};

export const resolveSignedReadUrl = async ({
  storedFileReference,
  getSignedUrl,
}: {
  storedFileReference?: string | null;
  getSignedUrl: (filepath: string) => Promise<SignedReadUrlResponse>;
}) => {
  if (!storedFileReference) return undefined;

  const response = await getSignedUrl(storedFileReference);
  if (!response.status || !response.url) return undefined;

  return response.url;
};

export const getStoredFileViewLink = (file: StoredFilePreviewSource) => {
  if (!file || typeof file !== "object") return undefined;
  if ("link" in file && typeof file.link === "string") return file.link;
  return undefined;
};

const isAbsoluteUrl = (value: string) => {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
};

export const getStoredFilePreviewUrl = ({
  file,
  getLegacyUrl,
}: {
  file: StoredFilePreviewSource;
  getLegacyUrl: (fileId: string) => string;
}) => {
  const link = getStoredFileViewLink(file);
  if (link) return link;
  if (typeof file === "string") {
    return isAbsoluteUrl(file) ? file : getLegacyUrl(file);
  }
  if (file instanceof File) return URL.createObjectURL(file);
  return undefined;
};

export const getBadgePreviewUrl = ({
  badge,
  getLegacyUrl,
}: {
  badge?: BadgeFileView | null;
  getLegacyUrl: (fileId: string) => string;
}) => {
  if (!badge) return undefined;
  return (
    badge.fileS3 ||
    getStoredFilePreviewUrl({ file: badge.file ?? badge.link, getLegacyUrl })
  );
};
